import { useState } from 'react'
import type { ProyectoPlan } from './tipos'
import {
  IconoGrafico,
  IconoAlerta,
  IconoCheck,
  IconoFlechaAbajo,
  IconoFlechaArriba,
} from '@/shared/ui/Iconos/Iconos'
import estilos from './ComparadorPresupuesto.module.css'

function formatoPesos(valor: number): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(Math.round(valor))
}

type Props = {
  proyecto: ProyectoPlan
  calculos: {
    transcurrido: number
    avanceGlobal: number
    totalDias: number
  } | null
}

export function ComparadorPresupuesto({ proyecto, calculos }: Props) {
  const [desgloseAbierto, setDesgloseAbierto] = useState(false)

  // 1. Presupuesto Total: asignado o sumatoria de módulos
  const sumaModulos = proyecto.etapas.reduce(
    (acc, e) => acc + e.modulos.reduce((sum, m) => sum + (Number(m.valor_total) || 0), 0),
    0
  )
  const presupuestoTotal = proyecto.presupuesto > 0 ? proyecto.presupuesto : sumaModulos

  // 2. Producido / Gastado a la fecha: sumatoria del avance ponderado en pesos
  const producidoTotal = proyecto.etapas.reduce(
    (acc, e) =>
      acc +
      e.modulos.reduce(
        (sum, m) => sum + ((Number(m.valor_total) || 0) * (Number(m.avance) || 0)) / 100,
        0
      ),
    0
  )

  // 3. Debería llevar hoy: porcentaje de tiempo transcurrido del proyecto por su valor
  const transcurrido = calculos?.transcurrido ?? 0
  const deberiaTotal = Math.round(presupuestoTotal * transcurrido)

  // 4. Brecha en pesos y estado semafórico
  const diferencia = producidoTotal - deberiaTotal
  const alDia = Math.abs(diferencia) <= Math.max(1, presupuestoTotal) * 0.02
  const atrasado = diferencia < 0 && !alDia

  // 5. Escala normalizada idéntica a Arkano AlDia
  const escala = Math.max(1, presupuestoTotal, deberiaTotal, producidoTotal)
  const x = (valor: number) => `${Math.max(0, Math.min(100, (valor / escala) * 100))}%`

  const pctProducido = presupuestoTotal > 0 ? Math.round((producidoTotal / presupuestoTotal) * 100) : 0
  const pctDeberia = presupuestoTotal > 0 ? Math.round((deberiaTotal / presupuestoTotal) * 100) : 0

  return (
    <section className={estilos.comparadorCard} aria-label="Comparador Presupuestal y Financiero">
      {/* Cabecera */}
      <div className={estilos.cabecera}>
        <div className={estilos.titulos}>
          <div className={estilos.tituloRow}>
            <span className={estilos.iconoTitulo} aria-hidden="true">
              <IconoGrafico />
            </span>
            <h3 className={estilos.titulo}>Comparador Presupuestal y Avance Financiero</h3>
            <span className={estilos.badgeProyecto}>{proyecto.codigo}</span>
          </div>
          <p className={estilos.subtitulo}>
            Compara el valor ejecutado real contra la meta monetaria programada a hoy. Cifras en dinero real.
          </p>
        </div>
      </div>

      {/* Grid de KPIs financieros */}
      <div className={estilos.kpisGrid}>
        <div className={estilos.kpiCaja}>
          <span className={estilos.kpiCajaEtiqueta}>Presupuesto Total</span>
          <b className={estilos.kpiCajaValor}>{formatoPesos(presupuestoTotal)}</b>
          <span className={estilos.kpiCajaSub}>{proyecto.etapas.length} etapas programadas</span>
        </div>

        <div className={estilos.kpiCaja}>
          <span className={estilos.kpiCajaEtiqueta}>Producido / Gastado</span>
          <b className={estilos.kpiCajaValor}>{formatoPesos(producidoTotal)}</b>
          <span className={estilos.kpiCajaSub}>{pctProducido}% del presupuesto total</span>
        </div>

        <div className={estilos.kpiCaja}>
          <span className={estilos.kpiCajaEtiqueta}>Meta Esperada a Hoy</span>
          <b className={estilos.kpiCajaValor}>{formatoPesos(deberiaTotal)}</b>
          <span className={estilos.kpiCajaSub}>Al ritmo del plazo ({pctDeberia}%)</span>
        </div>

        <div className={estilos.kpiCaja}>
          <span className={estilos.kpiCajaEtiqueta}>Desviación Financiera</span>
          <b
            className={`${estilos.kpiCajaValor} ${
              alDia ? estilos.kpiBrechaOk : atrasado ? estilos.kpiBrechaAlerta : estilos.kpiBrechaOk
            }`}
          >
            {alDia ? (
              'Al día'
            ) : atrasado ? (
              `-${formatoPesos(Math.abs(diferencia))}`
            ) : (
              `+${formatoPesos(diferencia)}`
            )}
          </b>
          <span className={estilos.kpiCajaSub}>
            {alDia ? (
              'Ritmo financiero equilibrado'
            ) : atrasado ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <IconoAlerta /> Por debajo de lo proyectado
              </span>
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <IconoCheck /> Por delante del cronograma
              </span>
            )}
          </span>
        </div>
      </div>

      {/* Gráfico de barras proporcional idéntico a Arkano AlDia */}
      <div className={estilos.graficoSeccion}>
        <div className={estilos.leyenda}>
          <span className={estilos.itemLeyenda}>
            <i className={`${estilos.muestraColor} ${estilos.colorProducido}`} />
            Producido / Gastado
          </span>
          {atrasado && (
            <span className={estilos.itemLeyenda}>
              <i className={`${estilos.muestraColor} ${estilos.colorBrecha}`} />
              Brecha por recuperar
            </span>
          )}
          <span className={estilos.itemLeyenda}>
            <i className={`${estilos.muestraColor} ${estilos.colorMeta}`} />
            Meta que tocaba a hoy
          </span>
          <span className={estilos.itemLeyenda}>
            <i className={`${estilos.muestraColor} ${estilos.colorPresupuesto}`} />
            Valor del presupuesto
          </span>
        </div>

        <div className={estilos.barraPistaTotal}>
          {/* Pista de valor total presupuestado */}
          <span
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              width: x(presupuestoTotal),
              background: '#e9e3f5',
              borderRadius: 7,
            }}
          />

          {/* Brecha rayada si hay atraso */}
          {atrasado && (
            <span
              className={estilos.barraBrechaAtraso}
              style={{
                left: x(producidoTotal),
                width: x(deberiaTotal - producidoTotal),
              }}
            />
          )}

          {/* Relleno de lo producido */}
          <span
            className={estilos.barraRellenoProducido}
            style={{ width: x(producidoTotal) }}
          />

          {/* Marcador vertical de la meta de hoy */}
          <span
            className={estilos.marcadorMetaHoy}
            style={{ left: x(deberiaTotal) }}
          >
            <span className={estilos.marcadorMetaPill}>Hoy: {pctDeberia}%</span>
          </span>
        </div>
      </div>

      {/* Desglose colapsable por etapas */}
      <div className={estilos.desgloseHeader}>
        <button
          type="button"
          className={estilos.btnAlternarDesglose}
          onClick={() => setDesgloseAbierto(!desgloseAbierto)}
        >
          {desgloseAbierto ? <IconoFlechaArriba /> : <IconoFlechaAbajo />}
          <span>{desgloseAbierto ? 'Ocultar desglose por etapas' : 'Ver desglose financiero por etapas'}</span>
        </button>
        <span className={estilos.subtitulo}>
          {proyecto.etapas.length} etapas analizadas
        </span>
      </div>

      {desgloseAbierto && (
        <div className={estilos.etapasLista}>
          {proyecto.etapas.map((etapa) => {
            const etapaPresupuesto = etapa.modulos.reduce(
              (acc, m) => acc + (Number(m.valor_total) || 0),
              0
            )
            const etapaProducido = etapa.modulos.reduce(
              (acc, m) =>
                acc + ((Number(m.valor_total) || 0) * (Number(m.avance) || 0)) / 100,
              0
            )
            const etapaPct =
              etapaPresupuesto > 0
                ? Math.round((etapaProducido / etapaPresupuesto) * 100)
                : 0

            return (
              <div key={etapa.id} className={estilos.filaEtapa}>
                <div className={estilos.filaEtapaInfo}>
                  <span className={estilos.etapaNombre}>{etapa.nombre}</span>
                  <div className={estilos.etapaNumeros}>
                    <span>
                      Ejecutado: <b>{formatoPesos(etapaProducido)}</b> de{' '}
                      <b>{formatoPesos(etapaPresupuesto)}</b>
                    </span>
                    <span>({etapaPct}%)</span>
                  </div>
                </div>
                <div className={estilos.barraEtapaPista}>
                  <div
                    className={estilos.barraEtapaRelleno}
                    style={{ width: `${Math.min(100, etapaPct)}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}
